import { useNavigate } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import VideosIcon from "../../assets/videos-icon.png";
import { useMatterPort } from "../../hooks/useMatterPort";
import { useCallback, useEffect, useRef, useState } from "react";
import { PropertiesVideoCard } from "../common/propertiesVideoCard";
import type { RealsProperty } from "../../interfaces/realsProperties";
import { usePropertyModalStore } from "../../stores/sharePropertyModalStore";
import { SharePropertyModal } from "../SharePropertyModal/sharePropertyModal";
import { useWalkthroughModalStore } from "../../stores/walkthroughModalStore";
import { WalkthroughModal } from "../WalkthroughModal/walkthroughModal";

const RealsView = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ axis: "y" });
  const { showPropertyModal } = usePropertyModalStore();
  const { isLoading, getVideos } = useMatterPort();
  const [currentPage, setCurrentPage] = useState(1);
  const [realsProperties, setRealsProperties] = useState<RealsProperty[]>([]);
  const navigate = useNavigate();
  const lastCardRef = useRef<HTMLDivElement | null>(null);
  const { showWalkthroughModal, setShowWalkthroughModal } =
    useWalkthroughModalStore();

  useEffect(() => {
    const shouldShowTopMatchesModal = !localStorage.getItem(
      "hasSeenWalkthroughModal"
    );

    if (shouldShowTopMatchesModal) {
      setShowWalkthroughModal(true);
      localStorage.setItem("hasSeenWalkthroughModal", "true");
    }
  }, [setShowWalkthroughModal]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!emblaApi) {
        return;
      }
      if (event.key === "ArrowDown") {
        emblaApi.scrollNext();
      } else if (event.key === "ArrowUp") {
        emblaApi.scrollPrev();
      }
    },
    [emblaApi]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    let lastScrollTime = 0;

    const handleWheel = (event: WheelEvent) => {
      const now = Date.now();
      if (now - lastScrollTime < 700) {
        return;
      }

      if (event.deltaY > 50) {
        emblaApi.scrollNext();
        lastScrollTime = now;
      } else if (event.deltaY < -50) {
        emblaApi.scrollPrev();
        lastScrollTime = now;
      }
    };

    const container = emblaApi.containerNode();
    container.addEventListener("wheel", handleWheel, { passive: true });

    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [emblaApi]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const matterPorts = getVideos(3, currentPage);
    setRealsProperties(matterPorts || []);
    setCurrentPage((prev) => prev + 1);
  }, [isLoading]);

  useEffect(() => {
    if (!lastCardRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          const properties = getVideos(5, currentPage);
          setCurrentPage((prev) => prev + 1);
          setRealsProperties((prev) => {
            if (properties) {
              return [...(prev || []), ...properties];
            }
            return prev;
          });
        }
      },
      {
        root: null,
        threshold: 0.5,
      }
    );

    observer.observe(lastCardRef.current);

    return () => {
      if (lastCardRef.current) {
        observer.unobserve(lastCardRef.current);
      }
    };
  }, [currentPage, getVideos]);

  return (
    <div
      className={`h-full w-full relative ${window.bridge?.adjustedContentInset.top ? "pt-bridge-inset-top" : ""}`}
    >
      <div
        className="h-full w-full flex flex-col items-center justify-center overflow-hidden"
        ref={emblaRef}
      >
        <div className="h-full flex flex-col">
          {realsProperties.map((property, index) => {
            const isLast = index === realsProperties.length - 1;

            return (
              <div
                key={index}
                ref={isLast ? lastCardRef : null}
                className="embla__slide h-full flex-shrink-0 relative"
              >
                <PropertiesVideoCard property={property} />
                {isLast && isLoading && (
                  <div className="absolute w-full z-10 bottom-0">
                    <div className="relative h-[5px] w-full bg-z-dark-moss-green rounded-full">
                      <div
                        className="absolute top-0 left-0 h-full w-1/2 rounded-full"
                        style={{
                          animation: "loading-bar 1.3s infinite",
                          background:
                            "linear-gradient(90deg, #fff5, rgba(230,230,230,0.891))",
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {!isLoading && (
            <div className="embla__slide h-full w-full flex-shrink-0 space-y-8">
              <div className="flex items-end justify-center relative w-full">
                <img src={VideosIcon} alt="" className="max-h-[250px] z-20" />
                <div className="absolute inset-0 bg-gradient-to-t from-transparent via-transparent to-[#C6EEAA] z-10 pointer-events-none" />
              </div>
              <div className="px-2 space-y-8 w-full">
                <div className="text-center flex flex-col items-center gap-2 px-2">
                  <div className="text-z-dark-moss-green font-medium text-5xl">
                    You&#x2019;ve watched it all!
                  </div>
                  <div className="text-black">
                    We&#x2019;ll show you more soon. In the meantime, check out
                    listings that match your style.
                  </div>
                </div>
                <button
                  onClick={() => {
                    navigate("/explore");
                  }}
                  type="button"
                  className="text-sm text-white bg-z-dark-moss-green w-full py-3 rounded-full font-semibold"
                >
                  View Listings
                </button>
              </div>
            </div>
          )}
        </div>
        {!showPropertyModal && showWalkthroughModal && <WalkthroughModal />}
        {showPropertyModal && <SharePropertyModal />}
      </div>
    </div>
  );
};

export { RealsView };
